import axios from 'axios'

declare global {
  interface Window {
    Clerk?: {
      session?: {
        getToken(): Promise<string | null>
      }
    }
  }
}

export const PrivateAxiosInstance = axios.create({
  baseURL: import.meta.env.VITE_API_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  withCredentials: true,
  timeout: 500000,
})

// Public axios instance for unauthenticated requests (like secure policy access)
export const PublicAxiosInstance = axios.create({
  baseURL: `${import.meta.env.VITE_API_URL}/public`,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 500000,
})

// Add request interceptor to include Clerk session token for cross-subdomain requests
PrivateAxiosInstance.interceptors.request.use(
  async (config) => {
    try {
      // Get Clerk session token for cross-subdomain authentication
      if (typeof window !== 'undefined' && window.Clerk?.session) {
        const sessionToken = await window.Clerk.session.getToken()
        if (sessionToken) {
          config.headers.Authorization = `Bearer ${sessionToken}`
        }
      }
    } catch (error) {
      // Only log in development, fail silently in production
      if (import.meta.env.DEV) {
        console.warn('Failed to get Clerk session token:', error)
      }
    }
    return config
  },
  (error) => {
    return Promise.reject(error)
  }
)

const refreshAccessToken = async () => {
  try {
    return true
  } catch (error) {
    console.error('Failed to refresh access token:', error)
    throw error
  }
}

PrivateAxiosInstance.interceptors.response.use(
  (response) => {
    // If the response is successful, just return it
    return response
  },
  async (error) => {
    const originalRequest = error.config

    if (
      error.response &&
      error.response.status === 401 &&
      error?.response?.data?.message === 'TOKEN_EXPIRED' &&
      !originalRequest._retry
    ) {
      originalRequest._retry = true // Prevent infinite loop

      try {
        // Attempt to refresh the access token
        await refreshAccessToken()

        // Retry the original request
        return PrivateAxiosInstance(originalRequest)
      } catch (refreshError) {
        // Handle token refresh failure (e.g., log out the user)
        console.error('Failed to refresh token:', refreshError)
        throw refreshError
      }
    }

    // If the error is not a 401, reject it
    return Promise.reject(error)
  }
)
